import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from './Logo';

const FooterColumn = ({ title, links }) => (
  <div className="flex flex-col gap-6">
    <h3 className="text-white font-black uppercase tracking-[0.25em] text-[10px] pb-2 border-b border-white/5 inline-block self-start">
      {title}
    </h3>
    <ul className="flex flex-col gap-4">
      {links.map((link, i) => (
        <li key={i}>
          {link.path.startsWith('http') || link.path.startsWith('mailto') ? (
            <a 
              href={link.path} 
              className="text-brand-gray hover:text-brand-cyan text-sm font-medium transition-all duration-300 flex items-center gap-2 group"
            >
              <span className="w-0 h-[1px] bg-brand-cyan group-hover:w-3 transition-all duration-300"></span>
              {link.name}
            </a>
          ) : (
            <Link 
              to={link.path} 
              className="text-brand-gray hover:text-brand-cyan text-sm font-medium transition-all duration-300 flex items-center gap-2 group"
            >
              <span className="w-0 h-[1px] bg-brand-cyan group-hover:w-3 transition-all duration-300"></span>
              {link.name}
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const currentYear = 2026;

  const solutionLinks = [
    { name: 'AI Sales Automation', path: '/ai-sales-automation' },
    { name: 'AI Appointment Booking', path: '/ai-appointment-booking-automation' },
    { name: 'AI Follow-Up Automation', path: '/ai-follow-up-automation' },
    { name: 'AI Lead Response Automation', path: '/ai-lead-response-automation' },
    { name: 'AI CRM Automation', path: '/ai-crm-automation' },
    { name: 'AI Customer Communication', path: '/ai-customer-communication-automation' },
    { name: 'AI Lead Conversion', path: '/ai-lead-conversion-automation' },
    { name: 'AI Sales Funnel Automation', path: '/ai-sales-funnel-automation' },
    { name: 'AI Chat For Business', path: '/ai-chat-automation-for-businesses' },
  ];

  const industryLinks = [
    { name: 'Roofing AI', path: '/industries/roofing-ai-automation' },
    { name: 'Med Spa AI', path: '/industries/med-spa-ai-automation' },
    { name: 'Dentist AI', path: '/industries/dentist-ai-automation' },
    { name: 'Real Estate AI', path: '/industries/real-estate-ai-automation' },
    { name: 'HVAC AI', path: '/industries/hvac-ai-automation' },
    { name: 'Law Firm AI', path: '/industries/law-firm-ai-automation' },
    { name: 'Solar AI', path: '/industries/solar-ai-automation' },
    { name: 'Automotive AI', path: '/industries/automotive-ai-automation' },
  ];

  const resourceLinks = [
    { name: 'Platform Features', path: '/features' },
    { name: 'Infrastructure Pricing', path: '/pricing' },
    { name: 'Interactive Demo', path: '/demo' },
    { name: 'Case Studies', path: '/case-studies' },
    { name: 'AI Automation Guides', path: '/resources' },
    { name: 'Official Blog', path: 'https://blog.getdmflow.com' },
    { name: 'Technical Support', path: '/contact' },
  ];

  const companyLinks = [
    { name: 'About GetDMFlow', path: '/about' },
    { name: 'Book A Demo', path: '/contact' },
    { name: 'Get Started', path: '/pricing' },
    { name: 'support@getdmflow.com', path: 'mailto:support@getdmflow.com' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms of Service', path: '/terms-of-service' },
    { name: 'Cookie Policy', path: '/cookie-policy' },
  ];

  return (
    <footer className="bg-brand-dark border-t border-white/5 pt-20 pb-12 relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute bottom-0 left-0 w-full h-[500px] bg-brand-blue/5 blur-[150px] rounded-full pointer-events-none -z-10"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 1. PREMIUM CTA AREA */}
        <div className="mb-24">
          <div className="glass-panel p-8 md:p-16 rounded-[3rem] border-brand-cyan/20 bg-gradient-to-r from-brand-blue/10 via-transparent to-brand-cyan/10 relative overflow-hidden group">
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
              <div className="max-w-2xl text-center lg:text-left">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tighter leading-none">
                  Ready To Automate Your <br /> <span className="text-gradient">Lead Conversion?</span>
                </h2>
                <p className="text-brand-gray text-lg font-medium leading-relaxed">
                  Book a demo to see how GetDMFlow helps businesses automate conversations, follow-ups, and appointment booking.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-6 shrink-0">
                <Link to="/book-demo" className="px-10 py-5 rounded-full bg-white text-brand-dark font-black text-sm uppercase tracking-widest hover:scale-105 transition-all shadow-[0_0_40px_rgba(255,255,255,0.2)]">
                  Book Demo
                </Link>
                <Link to="/demo" className="px-10 py-5 rounded-full glass-panel text-white font-black text-sm uppercase tracking-widest hover:bg-white/10 transition-all">
                  Watch AI Demo
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* 2. MAIN NAVIGATION GRID */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-12 lg:gap-8 mb-24">
          <FooterColumn title="Solutions" links={solutionLinks} />
          <FooterColumn title="Industries" links={industryLinks} />
          <FooterColumn title="Resources" links={resourceLinks} />
          <FooterColumn title="Company" links={companyLinks} />
          <FooterColumn title="Legal" links={legalLinks} />
        </div>

        {/* 3. BOTTOM BAR */}
        <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <Logo />
            <p className="text-[10px] text-brand-gray font-black uppercase tracking-[0.4em]">
              © {currentYear} GetDMFlow. All rights reserved.
            </p>
          </div>
          
          <div className="flex items-center gap-8">
            <div className="flex flex-col items-end gap-1">
               <div className="flex items-center gap-2 text-[10px] font-black text-brand-cyan uppercase tracking-widest bg-brand-cyan/10 px-3 py-2 rounded-lg border border-brand-cyan/20 w-fit">
                 <SafeIcon icon={FiIcons.FiMapPin} className="w-3 h-3" /> Calgary, Alberta, Canada
               </div>
               <p className="text-[9px] text-brand-gray font-black uppercase tracking-[0.3em] pr-1">Canadian-Based AI Infrastructure</p>
            </div>
            
            <div className="flex items-center gap-4">
              {[FiIcons.FiInstagram, FiIcons.FiLinkedin, FiIcons.FiFacebook, FiIcons.FiTwitter].map((icon, i) => (
                <a key={i} href="#" className="text-brand-gray hover:text-brand-cyan transition-colors duration-300">
                  <SafeIcon icon={icon} className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;